import { useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { ArrowRight, Check } from "lucide-react";

const INTERESTS = [
  { id: "rebuild", label: "Rebuild schools" },
  { id: "stem", label: "Teach STEM" },
  { id: "mentoring", label: "Mentor students" },
  { id: "girls", label: "Girls-in-STEM programs" },
  { id: "curriculum", label: "Curriculum design" },
  { id: "fundraising", label: "Fundraising" },
  { id: "photography", label: "Photography / storytelling" },
  { id: "technology", label: "Technology / IT" },
  { id: "medical", label: "Medical / wellbeing" },
  { id: "logistics", label: "Logistics on-site" },
  { id: "translation", label: "Translation" },
  { id: "anywhere", label: "Wherever you need me" },
];

export default function VolunteerForm() {
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    skills: "",
    interests: ["anywhere"],
    message: "",
  });
  const [submitting, setSubmitting] = useState(false);

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const toggleInterest = (id) => {
    setForm((f) => {
      const has = f.interests.includes(id);
      return { ...f, interests: has ? f.interests.filter((x) => x !== id) : [...f.interests, id] };
    });
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email) {
      toast.error("Name and email are required");
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/volunteers", form);
      toast.success("Thank you — we'll be in touch within a few days.");
      setForm({ name: "", email: "", phone: "", skills: "", interests: ["anywhere"], message: "" });
    } catch (e) {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section data-testid="volunteer-section" id="volunteer" className="bg-[var(--forest)] text-[var(--bone)] py-24 md:py-32 relative overflow-hidden">
      <div className="grain absolute inset-0" />
      <div className="container-x relative grid grid-cols-12 gap-8">
        <div className="col-span-12 md:col-span-5">
          <div className="overline text-[var(--terracotta)]">Volunteer · Contact</div>
          <h2 className="font-serif text-4xl md:text-5xl mt-4 leading-[1.05] tracking-[-0.02em]">
            Bring what you have.<br/>We will find a place for it.
          </h2>
          <p className="text-[var(--bone)]/70 mt-6 leading-relaxed max-w-md">
            Engineers, teachers, doctors, photographers, plumbers, retired carpenters,
            second-year students — we need all of you. Tell us a little, and we&apos;ll match
            you to a project.
          </p>
          <div className="mt-12 grid grid-cols-2 gap-4 text-sm">
            <div>
              <div className="overline text-[var(--bone)]/50">Email</div>
              <div className="mt-2 break-all">irenefoundation1@gmail.com</div>
            </div>
            <div>
              <div className="overline text-[var(--bone)]/50">Region</div>
              <div className="mt-2 font-mono">Africa · Nigeria</div>
            </div>
          </div>
        </div>

        <form onSubmit={submit} data-testid="volunteer-form" className="col-span-12 md:col-span-7 md:pl-12 md:border-l md:border-[var(--bone)]/20">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <label className="block">
              <span className="overline text-[var(--bone)]/60">Full name</span>
              <input data-testid="vol-name" className="editorial-input on-dark mt-2" value={form.name} onChange={(e) => update("name", e.target.value)} placeholder="Jane Akinwale" required />
            </label>
            <label className="block">
              <span className="overline text-[var(--bone)]/60">Email</span>
              <input data-testid="vol-email" type="email" className="editorial-input on-dark mt-2" value={form.email} onChange={(e) => update("email", e.target.value)} placeholder="jane@example.com" required />
            </label>
            <label className="block">
              <span className="overline text-[var(--bone)]/60">Phone (optional)</span>
              <input data-testid="vol-phone" className="editorial-input on-dark mt-2" value={form.phone} onChange={(e) => update("phone", e.target.value)} placeholder="+234 …" />
            </label>
            <label className="block">
              <span className="overline text-[var(--bone)]/60">Skills</span>
              <input data-testid="vol-skills" className="editorial-input on-dark mt-2" value={form.skills} onChange={(e) => update("skills", e.target.value)} placeholder="e.g. math tutor, photographer, engineer" />
            </label>
          </div>

          <div className="mt-10">
            <div className="overline text-[var(--bone)]/60">I want to help with — pick as many as you like</div>
            <div className="mt-4 flex flex-wrap gap-2">
              {INTERESTS.map((it) => {
                const active = form.interests.includes(it.id);
                return (
                  <button
                    key={it.id}
                    type="button"
                    data-testid={`vol-int-${it.id}`}
                    onClick={() => toggleInterest(it.id)}
                    className={`inline-flex items-center gap-2 px-3.5 py-2 text-xs border transition-all ${
                      active
                        ? "bg-[var(--terracotta)] border-[var(--terracotta)] text-[var(--bone)]"
                        : "border-[var(--bone)]/30 text-[var(--bone)]/85 hover:border-[var(--bone)]"
                    }`}
                  >
                    {active && <Check className="w-3 h-3" />} {it.label}
                  </button>
                );
              })}
            </div>
          </div>

          <label className="block mt-10">
            <span className="overline text-[var(--bone)]/60">A short note (optional)</span>
            <textarea data-testid="vol-message" rows={3} className="editorial-input on-dark mt-2 resize-none" value={form.message} onChange={(e) => update("message", e.target.value)} placeholder="Tell us what excites you about this work…" />
          </label>

          <button data-testid="vol-submit" type="submit" disabled={submitting} className="btn-primary mt-10 disabled:opacity-60">
            {submitting ? "Sending…" : "Send my application"} <ArrowRight className="w-4 h-4" />
          </button>
        </form>
      </div>
    </section>
  );
}
